<template>
  <div>
    <departement-list-add-new
      :is-add-new-departement-sidebar-active.sync="isAddNewDepartementSidebarActive"
      :role-options="roleOptions"
      @refetch-data="refetchData"
    />

    <departement-list-edit
      :is-edit-departement-sidebar-active.sync="isEditDepartementSidebarActive"
      :departement-id="departementId"
      :departement-name="departementName"
      @refetch-data="refetchData"
    />

    <app-page-shell
      title="Departements"
      subtitle="Manage internal departement names used by users and procurement workflows."
    >
      <app-list-toolbar
        :per-page="perPage"
        :per-page-options="perPageOptions"
        @update:perPage="perPage = $event"
      >
        <template #search>
          <div class="app-toolbar-actions">
            <b-form-input
              v-model="searchQuery"
              class="app-toolbar-search"
              placeholder="Search..."
            />
            <b-button
              variant="primary"
              @click="isAddNewDepartementSidebarActive = true"
            >
              <feather-icon icon="PlusIcon" />
              <span class="align-middle ml-50 text-nowrap">Add Departement</span>
            </b-button>
          </div>
        </template>
      </app-list-toolbar>

      <b-table
        ref="refDepartementListTable"
        class="position-relative"
        :items="fetchDepartements"
        responsive
        :fields="tableColumns"
        primary-key="departement_id"
        :sort-by.sync="sortBy"
        show-empty
        empty-text="No matching records found"
        :sort-desc.sync="isSortDirDesc"
        :busy="loading"
      >
        <template #table-busy>
          <b-skeleton-table
            :rows="perPage || 5"
            :columns="tableColumns ? tableColumns.length : 6"
            :table-props="{ bordered: true, striped: true }"
          />
        </template>

        <template #cell(departement_name)="data">
          <div class="text-nowrap">
            <span class="align-text-top text-capitalize">{{ data.item.departement_name }}</span>
          </div>
        </template>

        <template #cell(actions)="data">
          <b-dropdown
            variant="link"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>

            <b-dropdown-item @click="editDepartement(data.item)">
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">Edit</span>
            </b-dropdown-item>

            <b-dropdown-item @click="deleteDepartement(data.item.departement_id)">
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50">Delete</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>

      <app-pagination-footer
        :meta="dataMeta"
        :current-page="currentPage"
        :total-rows="totalDepartements"
        :per-page="perPage"
        @update:currentPage="currentPage = $event"
      />
    </app-page-shell>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormInput, BButton, BTable, BMedia, BAvatar, BLink,
  BBadge, BDropdown, BDropdownItem, BPagination, BSkeletonTable,
} from 'bootstrap-vue'
import axios from 'axios'
import vSelect from 'vue-select'
import store from '@/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { ref, onUnmounted } from '@vue/composition-api'
import { avatarText } from '@core/utils/filter'

import apiEndpoints from '@/constants/apiEndpoints'
import AppPageShell from '@/views/apps/components/AppPageShell.vue'
import AppListToolbar from '@/views/apps/components/AppListToolbar.vue'
import AppPaginationFooter from '@/views/apps/components/AppPaginationFooter.vue'
import useDepartementsList from './DepartementList'
import DepartementModule from './DepartementModule'
import DepartementListAddNew from './DepartementAddNew.vue'
import DepartementListEdit from './DepartementEdit.vue'

export default {
  components: {
    DepartementListEdit,
    DepartementListAddNew,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BSkeletonTable,
    vSelect,
    AppPageShell,
    AppListToolbar,
    AppPaginationFooter,
  },
  data() {
    return {
      userRole: '',
    }
  },
  setup() {
    const DEPARTEMENT_APP_STORE_MODULE_NAME = 'app-departement'

    if (!store.hasModule(DEPARTEMENT_APP_STORE_MODULE_NAME)) {
      store.registerModule(DEPARTEMENT_APP_STORE_MODULE_NAME, DepartementModule)
    }

    onUnmounted(() => {
      if (store.hasModule(DEPARTEMENT_APP_STORE_MODULE_NAME)) {
        store.unregisterModule(DEPARTEMENT_APP_STORE_MODULE_NAME)
      }
    })

    const isAddNewDepartementSidebarActive = ref(false)
    const isEditDepartementSidebarActive = ref(false)

    const departementId = ref('')
    const departementName = ref('')

    const statusOptions = [
      { label: 'Pending', value: 'pending' },
      { label: 'Active', value: 'active' },
      { label: 'Inactive', value: 'inactive' },
    ]

    const {
      fetchDepartements,
      tableColumns,
      perPage,
      currentPage,
      totalDepartements,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refDepartementListTable,
      refetchData,

      resolveDepartementRoleVariant,
      resolveDepartementRoleIcon,
      resolveDepartementStatusVariant,

      roleFilter,
      statusFilter,
      loading,
    } = useDepartementsList()

    const roleOptions = [
      { label: 'Admin', value: 'admin' },
      { label: 'Author', value: 'author' },
      { label: 'Editor', value: 'editor' },
      { label: 'Maintainer', value: 'maintainer' },
      { label: 'Subscriber', value: 'subscriber' },
    ]

    return {
      departementId,
      departementName,
      isEditDepartementSidebarActive,
      isAddNewDepartementSidebarActive,
      fetchDepartements,
      tableColumns,
      perPage,
      currentPage,
      totalDepartements,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refDepartementListTable,
      refetchData,
      avatarText,
      resolveDepartementRoleVariant,
      resolveDepartementRoleIcon,
      resolveDepartementStatusVariant,
      roleOptions,
      statusOptions,
      roleFilter,
      statusFilter,
      loading,
    }
  },
  mounted() {
    this.userRole = this.$cookies.get('UserRole')
    this.$root.$on('refreshTable', () => {
      if (this.$refs.refDepartementListTable) {
        if (this.$refs.refDepartementListTable) this.$refs.refDepartementListTable.refresh()
      }
    })
  },
  methods: {
    deleteDepartement(id) {
      const userToken = this.$cookies.get('userToken')
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userToken}`,
      }
      const url = `${process.env.VUE_APP_API_URL}${apiEndpoints.API_DELETE_DEPARTEMENTS}/${id}`

      axios
        .get(url, { headers })
        .then(response => {
          if (response.data.success === true) {
            if (this.$refs.refDepartementListTable) this.$refs.refDepartementListTable.refresh()
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: 'Success',
                icon: 'CheckIcon',
                variant: 'success',
                text: 'Your data has been deleted!',
              },
            })
          } else {
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: 'Notification',
                icon: 'BellIcon',
                variant: 'danger',
                text: response.data.message || 'Something went wrong',
              },
            })
          }
        })
        .catch(err => {
          console.error(err)
        })
    },
    editDepartement(data) {
      this.departementId = data.departement_id ? data.departement_id.toString() : ''
      this.departementName = data.departement_name
      this.isEditDepartementSidebarActive = true
    },
  },
}
</script>

<style lang="scss" scoped>
.app-toolbar-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: .75rem;
  min-width: 0;
}

.app-toolbar-search {
  width: 260px;
}

@media (max-width: 991.98px) {
  .app-toolbar-actions {
    align-items: stretch;
    flex-direction: column;
    width: 100%;
  }

  .app-toolbar-search {
    width: 100%;
  }
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
